import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dateResponseMap, hasPermissions } from '../../helpers';

import {
  reportFiscalDetailLoadingSelector,
  findFiscalDetailRequested,
  reportFiscalDetailFilterEndDateSelector,
  reportFiscalDetailFilterStartDateSelector,
  changeFiscalDetailFilterCountry,
  changeFiscalDetailFilterEndDate,
  changeFiscalDetailFilterStartDate,
  activeCountriesSelector,
  userPermissionsDataSelector,
  reprocessParametersRequested,
  filterParametersLoadedSelector,
  reprocessParametersSelector,
} from '../../state';

import { ReportFilterActions, ReportFilters } from '../ReportFilters';
import { AppPermissions, Country, sovosStatusKeys } from '../../types';
import { PermissionsProvider } from '../Permissions';
import { CountryFilter, ImportDateRangeFilter } from './filters';
import routes from '../../config/routes';
import StoreFilter from './filters/StoreFilter';

const EMPTY_COUNTRIES: Country[] = [];

const handleSearchParams = (
  dispatch: Dispatch<AnyAction>,
  searchParams: URLSearchParams,
  activeCountries: Country[]
) => {
  const paramCountryCode = searchParams.get(sovosStatusKeys.country);
  const dateStr = searchParams.get(sovosStatusKeys.importDate);

  if (!dateStr || activeCountries.length === 0) return;

  const date = dateResponseMap(dateStr) as Date;

  const countries: Country[] = activeCountries.filter(
    country => country.countryCode === paramCountryCode
  );

  if (date) {
    dispatch(changeFiscalDetailFilterCountry(countries));
    dispatch(changeFiscalDetailFilterStartDate(date));
    dispatch(changeFiscalDetailFilterEndDate(date));
    dispatch(findFiscalDetailRequested());
  }
};

const ButtonReprocess = () => {
  const navigate = useNavigate();
  const handleClickApply = useCallback(
    () => navigate(routes.protectedNestedRoutes.fiscalDetail.children.create.path),
    [navigate]
  );
  const { t } = useTranslation();
  const { loaded } = useSelector(activeCountriesSelector);
  const disabled = !loaded;

  return (
    <Button variant="contained" onClick={handleClickApply} disabled={disabled}>
      {t('navigation.buttons.reprocess')}
    </Button>
  );
};

const MastersDetailSearch = () => {
  const userPermissions = useSelector(userPermissionsDataSelector).map(i => i.permission);
  const startImportDate = useSelector(reportFiscalDetailFilterStartDateSelector).accept;
  const endImportDate = useSelector(reportFiscalDetailFilterEndDateSelector).accept;

  const activeCountriesResource = useSelector(activeCountriesSelector);

  const activeCountries = useMemo(
    () =>
      activeCountriesResource.loaded && activeCountriesResource.data
        ? activeCountriesResource.data
        : EMPTY_COUNTRIES,
    [activeCountriesResource.loaded, activeCountriesResource.data]
  );

  const applyDisabled = !(startImportDate && endImportDate);

  const isParameterLoaded = useSelector(filterParametersLoadedSelector);

  const reprocessRes = useSelector(reprocessParametersSelector);
  const reprocessLoaded = reprocessRes?.loaded;
  const reprocessLoading = reprocessRes?.loading;

  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  useEffect(() => {
    handleSearchParams(dispatch, searchParams, activeCountries);
  }, [dispatch, searchParams, activeCountries]);

  useEffect(() => {
    const shouldRequest = isParameterLoaded && !reprocessLoaded && !reprocessLoading;

    if (shouldRequest) {
      dispatch(reprocessParametersRequested());
    }
  }, [dispatch, isParameterLoaded, reprocessLoaded, reprocessLoading]);

  useEffect(() => {
    if (isParameterLoaded) {
      dispatch(reprocessParametersRequested());
    }
  }, [dispatch, isParameterLoaded]);
  return (
    <ReportFilters
      loadingSelector={reportFiscalDetailLoadingSelector}
      applyAction={findFiscalDetailRequested}
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.DASHBOARD_FISCAL_IMPORT]}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={2} lg={2} xl={2}>
            <CountryFilter fullWidth />
          </Grid>
          <Grid item xs={12} md={2} lg={2} xl={2}>
            <StoreFilter />
          </Grid>
          <Grid item xs={12} md={6} lg={4} xl={3}>
            <ImportDateRangeFilter spacing={1} />
          </Grid>
          <Grid item xs={12} md lg xl>
            <ReportFilterActions applyDisabled={applyDisabled}>
              {hasPermissions(userPermissions, AppPermissions.REPROCESS) && <ButtonReprocess />}
            </ReportFilterActions>
          </Grid>
        </Grid>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default MastersDetailSearch;
